import React from "react"

import { NotificationManager } from "react-notifications"
import { useHistory } from "react-router-dom"
import { gql, useMutation } from "urql"

import { Tooltip } from "@chakra-ui/core"
import {
  CalendarDaysIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/solid"

import { Color } from "~tailwindui/types/enums"

import { Button, StyledLink } from "~tailwindui/Basics"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"
import { RunMutation } from "src/types"

import { path } from "components/Tutor/TutorRoutes"

export type ScheduleSessionForStudentButtonProps = {
  className?: string
  student: {
    id: string
    firstName: string
    hasPrivateBillingMethod: boolean
    studyGroups: {
      id: string
      name: string
      tutorMaySchedule: boolean
      oneOnOne: boolean
      organization: {
        id: string
      }
    }[]
  }
}

const ScheduleSessionForStudentButton: React.FC<
  ScheduleSessionForStudentButtonProps
> = ({ className, student }) => {
  const history = useHistory()

  const [, runMutation]: [any, RunMutation] = useMutation(
    createPrivatePayOneOnOne
  )

  const scheduleableGroups = student.studyGroups.filter(
    group => group.tutorMaySchedule
  )

  const mayCreatePrivateOneOnOne =
    student.hasPrivateBillingMethod &&
    student.studyGroups.every(group => group.organization || !group.oneOnOne)

  const handleSubmit = () => {
    runMutation({ studentId: student.id })
      .then(
        result => {
          const { failures, studyGroup } = result.data.createPrivatePayOneOnOne
          if (failures.length > 0) {
            NotificationManager.error(
              "Unable to create study group. Please reach out to admin for help."
            )
          } else {
            history.push(
              path("studyGroupScheduleSession", { id: studyGroup.id })
            )
          }
        },
        () =>
          NotificationManager.error(
            "Unable to create study group. Please reach out to admin for help."
          )
      )
      .catch(() =>
        NotificationManager.error(
          "Unable to create study group. Please reach out to admin for help."
        )
      )
  }

  return scheduleableGroups.length > 0 || mayCreatePrivateOneOnOne ? (
    <Modal.Dialog
      buttonText="Schedule Session"
      buttonClassNames={classNames(
        buttonClassNames({ color: Color.Teal }),
        className
      )}
    >
      {closeModal => (
        <>
          <Modal.Header Icon={CalendarDaysIcon}>
            Schedule a Session
          </Modal.Header>
          <Modal.Body>
            {scheduleableGroups.length > 0 && (
              <div>
                Choose a study group to schedule a session with:
                <ul className="m-3 flex flex-wrap gap-4">
                  {scheduleableGroups.map(studyGroup => (
                    <StyledLink
                      key={studyGroup.id}
                      button
                      color={Color.Teal}
                      to={path("studyGroupScheduleSession", {
                        id: studyGroup.id,
                      })}
                    >
                      {studyGroup.name}
                    </StyledLink>
                  ))}
                </ul>
              </div>
            )}

            {mayCreatePrivateOneOnOne && (
              <div className="my-2">
                {scheduleableGroups.length > 0 ? "Or, if" : "If"} you have been
                asked to schedule a private pay session with {student.firstName}
                :
                <Button
                  onClick={handleSubmit}
                  color={Color.Orange}
                  className="ml-3 mt-3"
                >
                  <div className="flex items-center gap-2">
                    <span>Set up your first private 1-1 session</span>
                    <ChevronDoubleRightIcon className="h-5 w-5 text-white" />
                  </div>
                </Button>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={closeModal}>Close</Button>
          </Modal.Footer>
        </>
      )}
    </Modal.Dialog>
  ) : (
    <Tooltip
      label="If you need to schedule a session with this student, please contact support."
      className="rounded-md bg-gray-600 py-1 px-2 text-white"
      aria-label="If you need to schedule a session with this student, please contact support."
    >
      <div>
        <Button disabled className={classNames("cursor-help", className)}>
          Schedule Session
        </Button>
      </div>
    </Tooltip>
  )
}

const createPrivatePayOneOnOne = gql`
  mutation createPrivatePayOneOnOne($studentId: ID!) {
    createPrivatePayOneOnOne(studentId: $studentId) {
      studyGroup {
        id
      }
      failures {
        message
      }
    }
  }
`

export default ScheduleSessionForStudentButton
